import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { LocalizeAsyncPipeModule, LocalizeModule, ProgressBarModule } from '@mosaik/angular';
import {
  CompositeLocalizationProvider,
  CultureInfoProvider,
  ICultureInfoProvider,
  ILocalizationManager,
  ILocalizationProvider,
  LoaderLocalizationProvider,
  LocalizationManager,
  LocalizationManagerLocator,
  MapLocalizationProvider,
  ObjectExtensions
} from '@mosaik/core';
import { BusyService } from '@mosaik/presentation';
import { SurveyResultComponent } from '../components/survey-result/survey-result.component';
import { LocalTranslations } from '../../local-translations';
import { SurveyService } from '../services/survey.service';

/**
 * @internal
 */
export function cultureInfoProviderFactory(): ICultureInfoProvider {
  const supportedLanguages = ['de', 'en', 'fr', 'it'].filter(x => window.navigator.language.includes(x));
  return new CultureInfoProvider(supportedLanguages.length > 0 ? supportedLanguages.shift() : 'en');
}

/**
 * @internal
 */
export function localizationProviderFactory(): ILocalizationProvider {
  return new CompositeLocalizationProvider();
}

/**
 * @internal
 */
export function localizationManagerFactory(
  cultureInfoProvider: CultureInfoProvider,
  localizationProvider: CompositeLocalizationProvider
): ILocalizationManager {
  localizationProvider.registerProvider(
    new MapLocalizationProvider(
      new Map<string, Map<string, string>>()
        .set('de', ObjectExtensions.toMap<string>(LocalTranslations['de']))
        .set('en', ObjectExtensions.toMap<string>(LocalTranslations['en']))
        .set('fr', ObjectExtensions.toMap<string>(LocalTranslations['fr']))
        .set('it', ObjectExtensions.toMap<string>(LocalTranslations['it']))
    )
  );
  const localizationManager = new LocalizationManager(cultureInfoProvider, localizationProvider);
  LocalizationManagerLocator.setLocalizationManager(localizationManager);
  return localizationManager;
}

@NgModule({
  imports: [CommonModule, BrowserModule, LocalizeAsyncPipeModule, LocalizeModule, ProgressBarModule],
  declarations: [SurveyResultComponent],
  providers: [
    SurveyService,
    BusyService,
    { provide: CultureInfoProvider, useFactory: cultureInfoProviderFactory },
    {
      provide: LoaderLocalizationProvider,
      useFactory: localizationProviderFactory
    },
    {
      provide: LocalizationManager,
      useFactory: localizationManagerFactory,
      deps: [CultureInfoProvider, LoaderLocalizationProvider]
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SurveyElementModule implements DoBootstrap {
  private readonly _injector: Injector;

  public constructor(injector: Injector) {
    this._injector = injector;
  }

  public ngDoBootstrap(): void {
    const element = createCustomElement(SurveyResultComponent, {
      injector: this._injector
    });
    customElements.define('survey-result-view', element);
  }
}
